/**
 * PaymentMethodBadges - Shows available payment methods for a region
 * Displays visual badges for each supported payment method
 */
import React from 'react';
import { CreditCard, Smartphone, Wallet, Building2 } from 'lucide-react';

// Payment method icons and labels
const PAYMENT_METHOD_CONFIG = {
  card: {
    icon: CreditCard,
    label: 'Card',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  apple_pay: {
    icon: () => (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
    ),
    label: 'Apple Pay',
    color: 'text-gray-800',
    bg: 'bg-gray-100'
  },
  google_pay: {
    icon: () => (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C6.476 2 1.545 6.931 1.545 13s4.931 11 11 11c6.352 0 10.545-4.462 10.545-10.744 0-.747-.082-1.461-.236-2.151h-10.31.001z" fill="#4285F4"/>
      </svg>
    ),
    label: 'Google Pay',
    color: 'text-gray-800',
    bg: 'bg-gray-100'
  },
  upi: {
    icon: Smartphone,
    label: 'UPI',
    color: 'text-green-600',
    bg: 'bg-green-50'
  },
  netbanking: {
    icon: Building2,
    label: 'Net Banking',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50'
  },
  wallet: {
    icon: Wallet,
    label: 'Wallets',
    color: 'text-orange-600',
    bg: 'bg-orange-50'
  },
  sepa_debit: {
    icon: Building2,
    label: 'SEPA',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  ideal: {
    icon: Building2,
    label: 'iDEAL',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50'
  },
  klarna: {
    icon: Wallet,
    label: 'Klarna',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50'
  },
  pix: {
    icon: Smartphone,
    label: 'Pix',
    color: 'text-teal-600',
    bg: 'bg-teal-50'
  },
  boleto: {
    icon: CreditCard,
    label: 'Boleto',
    color: 'text-yellow-600',
    bg: 'bg-yellow-50'
  },
  gopay: {
    icon: Wallet,
    label: 'GoPay',
    color: 'text-green-600',
    bg: 'bg-green-50'
  },
  ovo: {
    icon: Wallet,
    label: 'OVO',
    color: 'text-teal-600',
    bg: 'bg-teal-50'
  },
  gcash: {
    icon: Wallet,
    label: 'GCash',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  grab_pay: {
    icon: Wallet,
    label: 'GrabPay',
    color: 'text-green-600',
    bg: 'bg-green-50'
  },
  paynow: {
    icon: Smartphone,
    label: 'PayNow',
    color: 'text-red-600',
    bg: 'bg-red-50'
  },
  konbini: {
    icon: Building2,
    label: 'Konbini',
    color: 'text-orange-600',
    bg: 'bg-orange-50'
  },
  samsung_pay: {
    icon: Smartphone,
    label: 'Samsung Pay',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  kakao_pay: {
    icon: Wallet,
    label: 'Kakao Pay',
    color: 'text-yellow-600',
    bg: 'bg-yellow-50'
  },
  promptpay: {
    icon: Smartphone,
    label: 'PromptPay',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  truemoney: {
    icon: Wallet,
    label: 'TrueMoney',
    color: 'text-orange-600',
    bg: 'bg-orange-50'
  },
  momo: {
    icon: Wallet,
    label: 'MoMo',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50'
  },
  zalopay: {
    icon: Wallet,
    label: 'ZaloPay',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  fpx: {
    icon: Building2,
    label: 'FPX',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  grabpay: {
    icon: Wallet,
    label: 'GrabPay',
    color: 'text-green-600',
    bg: 'bg-green-50'
  },
  afterpay: {
    icon: Wallet,
    label: 'Afterpay',
    color: 'text-teal-600',
    bg: 'bg-teal-50'
  },
  bank_transfer: {
    icon: Building2,
    label: 'Bank Transfer',
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  },
  oxxo: {
    icon: Building2,
    label: 'OXXO',
    color: 'text-red-600',
    bg: 'bg-red-50'
  }
};

const PaymentMethodBadges = ({ methods = [], compact = false, className = '' }) => {
  if (!methods || methods.length === 0) {
    return null;
  }

  // Limit display to 4 methods in compact mode
  const displayMethods = compact ? methods.slice(0, 4) : methods;
  const remainingCount = methods.length - displayMethods.length;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {displayMethods.map((method) => {
        const config = PAYMENT_METHOD_CONFIG[method] || {
          icon: CreditCard,
          label: method.replace(/_/g, ' '),
          color: 'text-gray-600',
          bg: 'bg-gray-50'
        };
        const Icon = config.icon;

        return (
          <div
            key={method}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}
            title={config.label}
          >
            {typeof Icon === 'function' ? <Icon /> : <Icon className="w-3.5 h-3.5" />}
            {!compact && <span>{config.label}</span>}
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div className="flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          +{remainingCount} more
        </div>
      )}
    </div>
  );
};

export default PaymentMethodBadges;
